import { CartUpdateEvent } from '@theme/events';

const FLAVOR_TITLES = {
  vanilla: 'Velvet Vanilla',
  cocoa450: 'Cocoa Hazelnut',
};

function starterComboKey(f1, f2) {
  if (f1 === 'vanilla' && f2 === 'vanilla') return 'vv_vv';
  if (f1 === 'cocoa450' && f2 === 'cocoa450') return 'ch_ch';
  return 'vv_ch';
}

function formatMoney(cents) {
  return '$' + (cents / 100).toFixed(2);
}

/**
 * Native Custom Element so listeners survive AJAX cart re-renders. This
 * region is patched via DOMParser + morph() after every cart update, and
 * per spec any <script> parsed that way never (re)executes - only
 * connectedCallback is guaranteed to fire regardless of how the node
 * entered the document.
 */
class CartBundleUpsellComponent extends HTMLElement {
  connectedCallback() {
    this.addEventListener('click', this.#handleClick);
  }

  disconnectedCallback() {
    this.removeEventListener('click', this.#handleClick);
  }

  #prices() {
    return {
      vanilla: Number(this.dataset.vanillaPrice || 0),
      cocoa450: Number(this.dataset.cocoa450Price || 0),
      cocoa900: Number(this.dataset.cocoa900Price || 0),
    };
  }

  #bundlePriceCents(type) {
    return type === 'starter' ? Number(this.dataset.starterPriceCents) : Number(this.dataset.monthlyPriceCents);
  }

  #starterVariants() {
    return {
      vv_vv: this.dataset.starterVvVvId,
      vv_ch: this.dataset.starterVvChId,
      ch_ch: this.dataset.starterChChId,
    };
  }

  #monthlyVariants() {
    return {
      vanilla: this.dataset.monthlyVanillaId,
      cocoa450: this.dataset.monthlyCocoa450Id,
    };
  }

  #selectedAddonFlavor(offerEl) {
    const picker = offerEl.querySelector('.cart-bundle-upsell__flavor-picker');
    if (!picker) return 'cocoa450';
    const selected = picker.querySelector('.cart-bundle-upsell__flavor-option.is-selected');
    return selected ? selected.dataset.flavor : 'cocoa450';
  }

  #updateSavings(offerEl) {
    const type = offerEl.dataset.offerType;
    const prices = this.#prices();
    const addonFlavor = this.#selectedAddonFlavor(offerEl);
    const bundlePrice = this.#bundlePriceCents(type);
    let sumCents;

    if (type === 'starter') {
      const fixedFlavor = offerEl.dataset.fixedFlavor;
      sumCents = prices[fixedFlavor] + prices[addonFlavor];
    } else {
      sumCents = prices[addonFlavor] + prices.cocoa900;
    }

    const savings = sumCents - bundlePrice;
    const priceEl = offerEl.querySelector('[data-role="price"]');
    const savingsEl = offerEl.querySelector('[data-role="savings"]');
    if (priceEl) priceEl.textContent = formatMoney(bundlePrice) + ' AUD';
    if (savingsEl) savingsEl.textContent = 'save ' + formatMoney(savings);
  }

  #handleClick = (event) => {
    const flavorBtn = event.target.closest('.cart-bundle-upsell__flavor-option');
    if (flavorBtn) {
      this.#handleFlavorClick(flavorBtn);
      return;
    }

    const swapBtn = event.target.closest('.cart-bundle-upsell__swap-button');
    if (swapBtn) {
      this.#handleSwapClick(swapBtn);
    }
  };

  #handleFlavorClick(btn) {
    const picker = btn.closest('.cart-bundle-upsell__flavor-picker');
    const offerEl = btn.closest('.cart-bundle-upsell__offer');
    if (!picker || !offerEl) return;

    picker.querySelectorAll('.cart-bundle-upsell__flavor-option').forEach((b) => {
      b.classList.remove('is-selected');
    });
    btn.classList.add('is-selected');
    this.#updateSavings(offerEl);
  }

  #handleSwapClick(swapBtn) {
    const offerEl = swapBtn.closest('.cart-bundle-upsell__offer');
    if (!offerEl || swapBtn.disabled) return;

    const type = offerEl.dataset.offerType;
    const lineKey = offerEl.dataset.lineKey;
    const addonFlavor = this.#selectedAddonFlavor(offerEl);
    let items, properties;

    if (type === 'starter') {
      const fixedFlavor = offerEl.dataset.fixedFlavor;
      const comboKey = starterComboKey(fixedFlavor, addonFlavor);
      properties = {
        'Flavor 1': FLAVOR_TITLES[fixedFlavor],
        'Flavor 2': FLAVOR_TITLES[addonFlavor],
      };
      items = [{ id: Number(this.#starterVariants()[comboKey]), quantity: 1, properties }];
    } else {
      properties = { '450g Flavor': FLAVOR_TITLES[addonFlavor] };
      items = [{ id: Number(this.#monthlyVariants()[addonFlavor]), quantity: 1, properties }];
    }

    const origText = swapBtn.textContent;
    swapBtn.disabled = true;
    swapBtn.textContent = 'Swapping…';

    fetch('/cart/add.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ items }),
    })
      .then((res) => {
        if (!res.ok) return res.json().then((d) => { throw new Error(d.description || 'Cart error'); });
        return res.json();
      })
      .then((addResponse) => {
        // /cart/add.js with an `items` array payload returns { items: [...] },
        // not a bare line-item object - unwrap it here.
        const addedItem = (addResponse && addResponse.items && addResponse.items[0]) || addResponse;
        return fetch('/cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ id: lineKey, quantity: 0 }),
        })
          .then((res) => {
            if (!res.ok) return res.json().then((d) => { throw new Error(d.description || 'Cart error'); });
            return res.json();
          })
          .then(() => addedItem);
      })
      .then((addedItem) => {
        return fetch('/cart.js')
          .then((res) => res.json())
          .then((cart) => {
            document.dispatchEvent(
              new CartUpdateEvent(addedItem, null, {
                source: 'cart-bundle-upsell',
                itemCount: cart.item_count,
                productId: addedItem.product_id,
              })
            );
          });
      })
      .catch((err) => {
        console.error('[cart-bundle-upsell] cart error:', err);
        swapBtn.textContent = 'Error — try again';
        swapBtn.disabled = false;
        setTimeout(() => {
          swapBtn.textContent = origText;
        }, 3000);
      });
  }
}

if (!customElements.get('cart-bundle-upsell-component')) {
  customElements.define('cart-bundle-upsell-component', CartBundleUpsellComponent);
}
